/**
 * =============================================================================
 * PasswordInput — Campo de senha com botão "olhinho" (mostrar/ocultar)
 * Versão: 1.0.0 (2026-08-10)
 *
 * Descrição:
 *   Wrapper sobre o <Input> do design system que adiciona:
 *     - Ícone de cadeado opcional à esquerda (padrão das telas de auth)
 *     - Botão de alternância de visibilidade à direita (Eye / EyeOff)
 *
 * Observações:
 *   - O botão usa type="button" e tabIndex={-1} para não interferir no fluxo
 *     de Tab nem submeter o formulário.
 *   - A visibilidade é estado local (nunca persistida).
 * =============================================================================
 */
import * as React from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Exibe o ícone de cadeado à esquerda (default: true). */
  showLockIcon?: boolean;
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, showLockIcon = true, disabled, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);

    return (
      <div className="relative group">
        {showLockIcon && (
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-primary pointer-events-none" />
        )}
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          disabled={disabled}
          // pr-12 garante que o texto não fique por baixo do botão do olho
          className={cn(showLockIcon && 'pl-12', 'pr-12', className)}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
          title={visible ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';
